export type AIFunctionType =
  | 'ticket_summary'
  | 'response_suggestion'
  | 'risk_detection'
  | 'campaign_suggestion'
  | 'activity_analysis'
  | 'stream_review'
  | 'mass_message'
  | 'professional_email'
  | 'legal_clause'
  | 'contract_review';

export interface AIHistoryEntry {
  id: string;
  type: AIFunctionType;
  title: string;
  query: string;
  result: string;
  timestamp: string;
}

export interface AIQuickAction {
  id: string;
  label: string;
  icon: string;
  prompt: string;
  type: AIFunctionType;
  category: string;
}

export const aiTypeLabel: Record<AIFunctionType, string> = {
  ticket_summary: 'Resumir ticket',
  response_suggestion: 'Sugerir respuesta',
  risk_detection: 'Detectar riesgo',
  campaign_suggestion: 'Sugerir campaña',
  activity_analysis: 'Analizar actividad',
  stream_review: 'Revisar stream caído',
  mass_message: 'Generar mensaje masivo',
  professional_email: 'Generar email profesional',
  legal_clause: 'Generar cláusula legal',
  contract_review: 'Revisar contrato',
};

export const aiTypeIcon: Record<AIFunctionType, string> = {
  ticket_summary: 'ri-file-list-3-line',
  response_suggestion: 'ri-lightbulb-line',
  risk_detection: 'ri-alert-line',
  campaign_suggestion: 'ri-megaphone-line',
  activity_analysis: 'ri-line-chart-line',
  stream_review: 'ri-signal-wifi-error-line',
  mass_message: 'ri-mail-send-line',
  professional_email: 'ri-mail-line',
  legal_clause: 'ri-scales-3-line',
  contract_review: 'ri-file-text-line',
};

export const aiTypeColor: Record<AIFunctionType, string> = {
  ticket_summary: 'bg-sky-100 text-sky-700',
  response_suggestion: 'bg-amber-100 text-amber-700',
  risk_detection: 'bg-red-100 text-red-700',
  campaign_suggestion: 'bg-emerald-100 text-emerald-700',
  activity_analysis: 'bg-violet-100 text-violet-700',
  stream_review: 'bg-orange-100 text-orange-700',
  mass_message: 'bg-teal-100 text-teal-700',
  professional_email: 'bg-indigo-100 text-indigo-700',
  legal_clause: 'bg-rose-100 text-rose-700',
  contract_review: 'bg-cyan-100 text-cyan-700',
};

export const aiQuickActions: AIQuickAction[] = [
  {
    id: 'ticket-summary',
    label: 'Resumir tickets abiertos',
    icon: 'ri-file-list-3-line',
    prompt: 'Resumí todos los tickets abiertos actualmente',
    type: 'ticket_summary',
    category: 'Tickets',
  },
  {
    id: 'response-suggestion',
    label: 'Sugerir respuesta a ticket',
    icon: 'ri-lightbulb-line',
    prompt: 'Sugerime una respuesta para el ticket #1045 de TechBeats sobre la API',
    type: 'response_suggestion',
    category: 'Tickets',
  },
  {
    id: 'risk-detection',
    label: 'Detectar radios en riesgo',
    icon: 'ri-alert-line',
    prompt: 'Analizá qué radios están en riesgo de abandono o inactividad',
    type: 'risk_detection',
    category: 'Radios',
  },
  {
    id: 'campaign-suggestion',
    label: 'Sugerir campaña',
    icon: 'ri-megaphone-line',
    prompt: 'Qué campañas de marketing debería lanzar esta semana basado en los datos actuales',
    type: 'campaign_suggestion',
    category: 'Marketing',
  },
  {
    id: 'activity-analysis',
    label: 'Analizar actividad',
    icon: 'ri-line-chart-line',
    prompt: 'Analizá la actividad de la plataforma este mes con métricas detalladas',
    type: 'activity_analysis',
    category: 'Radios',
  },
  {
    id: 'stream-review',
    label: 'Revisar stream caído',
    icon: 'ri-signal-wifi-error-line',
    prompt: 'Revisá las radios con stream caído o intermitente y diagnosticá posibles causas',
    type: 'stream_review',
    category: 'Radios',
  },
  {
    id: 'mass-message',
    label: 'Generar mensaje masivo',
    icon: 'ri-mail-send-line',
    prompt: 'Generá un mensaje masivo para todas las radios sobre la nueva actualización de la plataforma',
    type: 'mass_message',
    category: 'Contenido',
  },
  {
    id: 'professional-email',
    label: 'Generar email profesional',
    icon: 'ri-mail-line',
    prompt: 'Redactá un email profesional para comunicar el nuevo plan de precios a los clientes',
    type: 'professional_email',
    category: 'Contenido',
  },
  {
    id: 'legal-clause',
    label: 'Generar cláusula legal',
    icon: 'ri-scales-3-line',
    prompt: 'Generá una cláusula de protección de datos para los términos de servicio de la plataforma',
    type: 'legal_clause',
    category: 'Legal',
  },
  {
    id: 'contract-review',
    label: 'Revisar contrato',
    icon: 'ri-file-text-line',
    prompt: 'Revisá el contrato estándar de RadioFreeWeb y señalá posibles riesgos o mejoras',
    type: 'contract_review',
    category: 'Legal',
  },
];

export function getAIResponse(type: AIFunctionType, _query: string): string {
  const responses: Record<AIFunctionType, string> = {
    ticket_summary:
      'Actualmente hay 8 tickets abiertos:\n\n🔴 Urgentes (3): #1042 Jazz Club (storage), #1045 TechBeats (API), #1048 Radio Élite (lives)\n🟡 En progreso (2): #1041 asignado a Carlos, #1043 asignado a Diana\n🔵 Bajos (2): #1040 consulta general, #1046 facturación\n⚪ Sin asignar (1): #1047 pendiente de revisión\n\nTiempo promedio de respuesta: 4.2h. El ticket #1045 de TechBeats lleva 28h sin respuesta. Recomiendo asignarlo urgente.',

    response_suggestion:
      'Sugerencia para el ticket #1045 (TechBeats - API):\n\n"Hola equipo de TechBeats,\n\nGracias por contactarnos. Actualmente la API completa de RadioFreeWeb está disponible a partir del plan Business, que incluye endpoints REST para gestión de programación, métricas en tiempo real, y webhooks personalizados.\n\nSin embargo, entendemos tu interés y podemos ofrecerte acceso anticipado a nuestro endpoint gratuito limitado para que puedas evaluar la integración. Incluye:\n\n• Búsqueda de radios por frecuencia\n• Metadatos básicos de streaming\n• 1000 requests/mes\n\n¿Te interesaría que te active este acceso mientras tanto? También podemos agendar una demo personalizada de la API completa."',

    risk_detection:
      'Detecté 4 radios en riesgo:\n\n1. Clásica FM — 45 días sin publicar contenido, audiencia cayó 62% en el último trimestre, 0 lives en 60 días\n2. Radio Sonora Digital — Stream intermitente con 12 caídas en 7 días, pérdida de oyentes del 35%\n3. Radio Marítima — 35 días sin login al panel, storage al 88%, plan Starter\n4. Jazz Club Radio — 30 días sin actividad, sin respuestas a emails de soporte\n\nRecomendación: Contacto proactivo urgente con Clásica FM. Para las demás, email de reenganche con beneficio temporal.',

    campaign_suggestion:
      'Campañas recomendadas para esta semana:\n\n1. Campaña de upgrade Starter → Pro\n   • Audiencia: Radio Marítima, Radio Sonora Digital, Clásica FM\n   • Canal: Email + Banner admin\n   • Hook: "Desbloqueá todo el potencial de tu radio"\n   • Potencial: +$68/mes en revenue\n\n2. Campaña de reenganche\n   • Audiencia: Radios inactivas (4 radios)\n   • Canal: Email personalizado\n   • Beneficio: 15% off por 3 meses al reactivar\n\n3. Campaña de adopción de App Oficial\n   • Audiencia: Radios Starter y Básico\n   • Canal: Notificación interna + Banner\n   • Objetivo: Aumentar instalaciones de la app\n\nPrioridad: Campaña de upgrade (mayor retorno inmediato).',

    activity_analysis:
      'Análisis de actividad — Junio 2026:\n\n📊 General:\n• 12 radios activas de 12 totales (100%)\n• 45 lives transmitidas (+15% vs mayo)\n• 230 fotos subidas (+8%)\n• 18 videos publicados (+22%)\n• 1,240 mensajes en cabina\n\n⏰ Horas pico: 18:00-22:00 (43% de la actividad)\n📅 Días pico: Viernes y Sábado (38% de la actividad semanal)\n\n🏆 Top 3 radios más activas:\n1. Radio Nueva Actitud — 8 lives, 54 fotos\n2. TechBeats — 7 lives, 38 fotos\n3. Radio Urbana 101 — 6 lives, 42 fotos\n\n📈 Tendencia: Crecimiento sostenido del 12% mensual en actividad general. La adopción de lives creció más que cualquier otra funcionalidad.',

    stream_review:
      'Revisión de streams — Diagnóstico:\n\n🔴 Streams caídos (1):\n• Onda Retro FM — Caído desde 2026-06-09 14:30 (47h). Último bitrate registrado: 64 kbps. Posible causa: servidor de origen no responde. Recomiendo verificar URL del stream y hosting.\n\n🟡 Streams intermitentes (2):\n• Radio Sonora Digital — 12 caídas en 7 días. Patrón: caídas cada 4-6 horas de 2-5 min. Posible causa: encoding inestable o límite de conexiones del host.\n• Clásica FM — 5 micro-cortes esta semana. Bitrate fluctuante entre 96-128 kbps.\n\n🟢 Streams estables (9): OK\n\nAcciones recomendadas:\n1. Contactar a Onda Retro FM urgente (notificación automática ya enviada)\n2. Sugerir a Radio Sonora Digital verificar configuración de encoder\n3. Monitorear Clásica FM por 48h más antes de escalar',

    mass_message:
      'ASUNTO: Nueva actualización de RadioFreeWeb — Junio 2026\n\nCANAL: Email + Notificación interna\n\nDESTINATARIOS: Todas las radios\n\nCUERPO:\n\n¡Hola equipo de {NOMBRE_RADIO}!\n\nEn RadioFreeWeb seguimos mejorando la plataforma para vos. Te contamos las novedades de junio:\n\n✨ ¿Qué hay de nuevo?\n\n• Lives en HD: Ahora podés transmitir en calidad HD (1080p) desde tu panel.\n• Estadísticas avanzadas: Nuevo dashboard con métricas de audiencia en tiempo real.\n• App oficial actualizada: Mejor experiencia para tus oyentes en iOS y Android.\n• Storage ampliado: +10 GB gratis para todos los planes.\n\n📅 Webinar exclusivo: "Cómo hacer crecer tu radio online"\nJueves 20 de junio · 18:00 (ART)\n\n👉 Entrá a tu panel para ver todas las novedades.\n\nSaludos,\nEl equipo de RadioFreeWeb',

    professional_email:
      'ASUNTO: Actualización de planes y precios — RadioFreeWeb 2026\n\nEstimado/a cliente,\n\nQueremos compartirte las mejoras que implementamos en nuestros planes a partir del 1 de julio de 2026. Estos cambios reflejan nuestro compromiso de ofrecerte el mejor servicio para tu radio online.\n\nNOVEDADES POR PLAN:\n\nStarter (se mantiene en $9/mes)\n• Storage: 5 GB → 10 GB\n• Lives mensuales: 4 → 6\n• App oficial incluida\n\nBásico ($19/mes)\n• Storage: 20 GB → 35 GB\n• Lives ilimitadas (antes 12)\n• Analíticas avanzadas (nuevo)\n• Dominio personalizado incluido\n\nPro ($39/mes)\n• Storage: 50 GB → 80 GB\n• API acceso anticipado (nuevo)\n• Hasta 5 colaboradores (antes 3)\n• Sin publicidad en el reproductor\n\nBusiness ($79/mes)\n• Storage: 150 GB → 250 GB\n• API completa (nuevo)\n• Hasta 15 colaboradores\n• Soporte prioritario 24/7\n\nPara los clientes actuales, estos cambios se aplicarán automáticamente en tu próxima facturación sin costo adicional.\n\nSi tenés alguna consulta, no dudes en responder a este email o contactarnos por el chat de soporte en tu panel.\n\nGracias por ser parte de RadioFreeWeb.\n\nEl equipo de RadioFreeWeb',

    legal_clause:
      'CLÁUSULA DE PROTECCIÓN DE DATOS PERSONALES\n\n1. Objeto. La presente cláusula regula el tratamiento de los datos personales recolectados por RadioFreeWeb (en adelante, "la Plataforma") en el marco de la prestación de sus servicios de streaming y gestión de radios online.\n\n2. Datos recolectados. La Plataforma podrá recolectar los siguientes datos: (a) datos de registro: nombre, correo electrónico, país de residencia; (b) datos de la radio: nombre, frecuencia, categoría, descripción; (c) datos de uso: estadísticas de audiencia, actividad en el panel, logs de acceso; (d) datos de facturación: información necesaria para procesar pagos.\n\n3. Finalidad. Los datos serán tratados exclusivamente para: (a) proveer y mejorar los servicios contratados; (b) facturación y gestión de cuentas; (c) comunicaciones operativas y de servicio; (d) análisis estadístico anonimizado.\n\n4. Base legal. El tratamiento se fundamenta en: (a) la ejecución del contrato de servicios; (b) el consentimiento expreso del titular para comunicaciones comerciales; (c) el interés legítimo de la Plataforma para mejoras del servicio.\n\n5. Conservación. Los datos se conservarán mientras dure la relación contractual y por un plazo máximo de 5 años posteriores a su finalización para fines de auditoría y cumplimiento legal.\n\n6. Derechos del titular. El titular podrá ejercer los derechos de acceso, rectificación, cancelación y oposición mediante solicitud a soporte@radiofreeweb.com.\n\n7. Transferencias. La Plataforma no cederá datos a terceros sin consentimiento expreso, salvo obligación legal o proveedores de infraestructura bajo acuerdos de confidencialidad.\n\n8. Seguridad. Se implementan medidas técnicas y organizativas para garantizar la seguridad de los datos, incluyendo cifrado en tránsito y en reposo, controles de acceso y auditorías periódicas.',

    contract_review:
      'REVISIÓN DEL CONTRATO ESTÁNDAR — RadioFreeWeb\n\nPuntos fuertes:\n✅ Cláusulas claras sobre propiedad intelectual del contenido (la radio mantiene todos los derechos)\n✅ Proceso de cancelación simple (aviso de 30 días, sin penalidad)\n✅ SLA definido: 99.5% uptime garantizado\n✅ Precios transparentes sin costos ocultos\n\nRiesgos detectados:\n⚠️ Cláusula 7.3 — La plataforma se reserva el derecho de modificar precios con 15 días de aviso. Recomiendo extender a 30 días para alinearlo con el plazo de cancelación.\n\n⚠️ Cláusula 12.1 — La limitación de responsabilidad es muy amplia. Sugiero acotarla a daños directos y establecer un tope de 12 meses de servicio.\n\n⚠️ Cláusula 9.4 — No especifica qué sucede con los datos al cancelar. Recomiendo agregar: "El cliente podrá exportar sus datos durante 30 días posteriores a la cancelación."\n\n⚠️ Cláusula 15.2 — Jurisdicción aplicable fija sin opción de arbitraje. Evaluar incluir cláusula de mediación previa.\n\nMejoras sugeridas:\n📝 Agregar cláusula de protección de datos alineada con GDPR/LGPD\n📝 Incluir plan de recuperación ante desastres (DRP) en el SLA\n📝 Especificar canales y tiempos de soporte por plan\n📝 Agregar cláusula de no competencia para radios (evitar spam entre radios)\n\nPuntaje general del contrato: 7.8/10 — Sólido pero con margen de mejora en las áreas señaladas.',
  };

  return responses[type] || 'No tengo una respuesta específica para esta consulta. Probá con una de las acciones rápidas disponibles.';
}

export const aiInitialHistory: AIHistoryEntry[] = [
  {
    id: 'h1',
    type: 'risk_detection',
    title: 'Detección de radios en riesgo',
    query: 'Analizá qué radios están en riesgo de abandono o inactividad',
    result: 'Detecté 4 radios en riesgo:\n\n1. Clásica FM — 45 días sin publicar contenido, audiencia cayó 62% en el último trimestre...\n2. Radio Sonora Digital — Stream intermitente con 12 caídas en 7 días...\n3. Radio Marítima — 35 días sin login al panel...\n4. Jazz Club Radio — 30 días sin actividad...',
    timestamp: '2026-06-10 16:45',
  },
  {
    id: 'h2',
    type: 'ticket_summary',
    title: 'Resumen de tickets',
    query: 'Resumí todos los tickets abiertos actualmente',
    result: 'Actualmente hay 8 tickets abiertos. 3 urgentes (2 sin asignar), 2 en progreso, 1 esperando cliente, 2 bajos. Tiempo promedio de respuesta: 4.2h.',
    timestamp: '2026-06-10 14:30',
  },
  {
    id: 'h3',
    type: 'campaign_suggestion',
    title: 'Campaña de upgrade',
    query: 'Qué campañas de marketing debería lanzar esta semana',
    result: 'Campañas recomendadas: 1. Upgrade Starter → Pro (3 radios, potencial +$68/mes), 2. Reenganche de inactivas (4 radios), 3. Adopción de App Oficial...',
    timestamp: '2026-06-10 11:15',
  },
  {
    id: 'h4',
    type: 'stream_review',
    title: 'Revisión de stream caído',
    query: 'Revisá las radios con stream caído o intermitente',
    result: 'Streams caídos: Onda Retro FM (47h). Streams intermitentes: Radio Sonora Digital (12 caídas), Clásica FM (5 micro-cortes). Acciones recomendadas: contacto urgente a Onda Retro FM...',
    timestamp: '2026-06-10 09:00',
  },
];